/**
 * The soundboard: what everything is made of, and what it sounds like.
 *
 * Two ideas carry the whole file.
 *
 * **A sound is chosen by impactor × surface.** A spear striking a steel helm
 * rings; the same spear on a golem's masonry is a dull scrape with no ring at
 * all; on a linen cape it is barely a thump. Deciding by the attacker alone —
 * which is what the game used to do — throws that away.
 *
 * **Pairings share families.** Nineteen material pairings do not need nineteen
 * hand-tuned cues. They map onto twelve *families*, each shifted in pitch and
 * level per pairing, which is both far less to author and far more consistent:
 * everything struck by stone belongs to the same sonic world.
 *
 * Nothing here touches the Web Audio API. It is data plus one lookup, so it can
 * be checked without a sound card.
 */

/* ------------------------------------------------------------------ */
/* Layer helpers — a cue is a transient, a body and a tail             */
/* ------------------------------------------------------------------ */

const noise = (cutoff, decay, gain, { filter = 'lowpass', q = 1, at = 0 } = {}) =>
  ({ src: 'noise', filter, cutoff, q, decay, gain, at });

const tone = (src, freq, decay, gain, { to, at = 0, attack = 0.004 } = {}) =>
  ({ src, freq, to, decay, gain, at, attack });

/* ------------------------------------------------------------------ */
/* Materials                                                           */
/* ------------------------------------------------------------------ */

export const MATERIAL = {
  STEEL: 'steel',
  WOOD: 'wood',
  STONE: 'stone',
  CLOTH: 'cloth',
  GLASS: 'glass',
  FLESH: 'flesh',
  ARCANE: 'arcane'
};

/** What each attacker strikes with. Casters never make contact at all. */
export const IMPACTOR_BY_MANNER = {
  pierce: MATERIAL.STEEL,   // spearhead — kept for the manner's own fallback
  slash: MATERIAL.STEEL,    // the pawn's bronze-cast sword
  cleave: MATERIAL.STEEL,   // sword
  charge: MATERIAL.WOOD,    // the lance is a wooden shaft
  crush: MATERIAL.STONE,    // a golem's fists
  bombard: MATERIAL.STEEL,  // the castle's iron shot
  // A shut book is board and paper: the wood row is the closest thing already
  // in the matrix — a dull clack with no ring — and it saves inventing a
  // leather row and six cells to go with it.
  swat: MATERIAL.WOOD,
  arcane: MATERIAL.ARCANE
};

/**
 * impactor × surface → a family, and how to shift it.
 *
 * `p` is a pitch multiplier and `g` a level multiplier, which is what lets one
 * family cover several pairings without sounding recycled.
 */
const MATRIX = {
  steel: {
    steel: { cue: 'ring', p: 1.0, g: 1.0 },
    wood: { cue: 'knock', p: 1.0, g: 0.9 },
    stone: { cue: 'scrape', p: 1.0, g: 0.8 },
    cloth: { cue: 'thump', p: 1.1, g: 0.55 },
    glass: { cue: 'shatter', p: 1.0, g: 0.9 },
    flesh: { cue: 'rend', p: 1.0, g: 0.85 }
  },
  wood: {
    steel: { cue: 'splinter', p: 1.0, g: 1.0 },
    wood: { cue: 'clack', p: 1.0, g: 0.9 },
    // The lance shattering on a golem: the same splintering, lower and bigger.
    stone: { cue: 'splinter', p: 0.82, g: 1.15 },
    cloth: { cue: 'thump', p: 0.9, g: 0.5 },
    glass: { cue: 'shatter', p: 1.08, g: 0.7 },
    flesh: { cue: 'rend', p: 0.9, g: 0.7 }
  },
  stone: {
    steel: { cue: 'clang', p: 1.0, g: 1.0 },
    wood: { cue: 'crunch', p: 1.0, g: 0.95 },
    stone: { cue: 'crack', p: 1.0, g: 1.0 },
    cloth: { cue: 'thump', p: 0.7, g: 0.7 },
    glass: { cue: 'shatter', p: 0.85, g: 1.0 },
    flesh: { cue: 'rend', p: 0.78, g: 0.9 }
  },
  // Nothing is struck, so the surface makes no difference — the only
  // percussive sound in a caster's fight is armour hitting the board after.
  arcane: {
    default: { cue: 'swell', p: 1.0, g: 1.0 }
  }
};

/** What wreckage sounds like landing on the board, which is wood. */
const LANDING = {
  steel: { cue: 'landSteel', p: 1.0, g: 1.0 },
  wood: { cue: 'landWood', p: 1.0, g: 0.9 },
  stone: { cue: 'landStone', p: 1.0, g: 1.0 },
  cloth: { cue: 'landCloth', p: 1.0, g: 0.8 },
  glass: { cue: 'landGlass', p: 1.0, g: 0.9 },
  flesh: { cue: 'landCloth', p: 0.75, g: 1.1 }
};

/**
 * The blow for one matchup.
 * @returns {{cue: string, pitch: number, gain: number}}
 */
export function resolveImpact(impactor, surface) {
  const row = MATRIX[impactor] ?? MATRIX.steel;
  const cell = row.default ?? row[surface] ?? row.steel;
  return { cue: cell.cue, pitch: cell.p, gain: cell.g };
}

/** The sound of a piece of wreckage landing. */
export function resolveLanding(material) {
  const cell = LANDING[material] ?? LANDING.steel;
  return { cue: cell.cue, pitch: cell.p, gain: cell.g };
}

/* ------------------------------------------------------------------ */
/* Per-character boards                                                */
/* ------------------------------------------------------------------ */

/**
 * Each piece owns a timbre, so a fight is identifiable with eyes shut: the
 * pawn is light and bright, the golem low and dry, the casters never strike
 * anything at all.
 *
 * `pitch` shifts that piece's whole impact vocabulary, which is what makes a
 * king's sword read heavier than a pawn's spear without separate cues.
 *
 * The creature voices are the part most at risk of feeling out of place, so
 * they are quiet, short, and fired at most once per fight.
 */
export const PIECE_SOUND = {
  p: { pitch: 1.28, gain: 0.85, move: 'stepLeather', effort: 'grunt', attack: 'attackPawn', hurt: 'hurtPawn' },
  n: { pitch: 0.95, gain: 1.0, move: 'gallop', charge: 'snort', fall: 'whinny', attack: 'attackKnight', hurt: 'hurtKnight' },
  // The bishop shares the wood row with the knight, because a book's boards
  // and a lance's shaft are the same material. What separates them is here: a
  // shut book is broad, flat and papery, so it lands lower and softer than a
  // thin shaft cracking. These two numbers touch impacts only.
  b: { pitch: 0.86, gain: 0.72, move: 'glide', cast: 'chant', attack: 'attackBishop', hurt: 'hurtBishop' },
  r: { pitch: 0.72, gain: 1.15, move: 'grind', death: 'groan', attack: 'attackRook', hurt: 'hurtRook' },
  q: { pitch: 1.15, gain: 0.9, move: 'glide', cast: 'cry', attack: 'attackQueen', hurt: 'hurtQueen' },
  k: { pitch: 0.82, gain: 1.05, move: 'stepPlate', effort: 'shout', attack: 'attackKing', hurt: 'hurtKing' }
};

/**
 * Which drop-in voice pool a piece type reaches for (public/audio/sfx):
 * everyone shares `male` except the queen, who gets her own — see
 * public/audio/sfx/README.md. Purely a sample-selection concern; the
 * pitch/gain table above still shapes every piece's own timbre on top of
 * whichever sample (or synthesized fallback) plays.
 */
export const POOL_BY_TYPE = { p: 'male', n: 'male', b: 'male', r: 'male', k: 'male', q: 'queen' };

/* ------------------------------------------------------------------ */
/* The cues                                                            */
/* ------------------------------------------------------------------ */

export const CUES = {
  /* ---- blows ---- */

  // Steel on steel. The bright tick, a resonant body, and two inharmonic
  // partials that keep ringing — the ring is the whole character.
  ring: { layers: [
    noise(4400, 0.03, 0.40, { filter: 'highpass' }),
    noise(2400, 0.09, 0.24, { filter: 'bandpass', q: 7 }),
    tone('triangle', 1420, 0.45, 0.13, { to: 1360, at: 0.004 }),
    tone('triangle', 2180, 0.34, 0.08, { to: 2100, at: 0.006 })
  ] },

  // Steel into wood: a knock with a little splinter and no ring.
  knock: { layers: [
    noise(2600, 0.035, 0.34, { filter: 'lowpass' }),
    noise(760, 0.09, 0.22, { filter: 'bandpass', q: 3 }),
    tone('sine', 190, 0.13, 0.20, { to: 120, at: 0.003 })
  ] },

  // Steel on masonry. Grit and a dull body — deliberately no ring at all,
  // which is what makes a spear against a golem sound futile.
  scrape: { layers: [
    noise(1500, 0.06, 0.30, { filter: 'lowpass' }),
    noise(2600, 0.05, 0.16, { filter: 'bandpass', q: 1.5 }),
    noise(3400, 0.07, 0.05, { filter: 'highpass', at: 0.008 }),
    tone('sine', 128, 0.10, 0.20, { to: 96, at: 0.004 })
  ] },

  // Anything into cloth: soft, low, gone immediately.
  thump: { layers: [
    noise(700, 0.09, 0.30, { filter: 'lowpass' }),
    tone('sine', 96, 0.13, 0.24, { to: 68, at: 0.004 })
  ] },

  // Glass. A bright burst and three short partials falling away.
  shatter: { layers: [
    noise(6000, 0.04, 0.28, { filter: 'highpass' }),
    tone('triangle', 3300, 0.16, 0.10),
    tone('triangle', 4200, 0.12, 0.07, { at: 0.012 }),
    tone('triangle', 2650, 0.22, 0.08, { at: 0.02 }),
    noise(3000, 0.24, 0.10, { filter: 'bandpass', q: 4, at: 0.02 })
  ] },

  // A wooden shaft failing: the crack, then the splinters.
  splinter: { layers: [
    noise(1300, 0.045, 0.34, { filter: 'bandpass', q: 2 }),
    tone('square', 300, 0.09, 0.16, { to: 120, at: 0.002 }),
    noise(2600, 0.20, 0.14, { filter: 'highpass', at: 0.03 })
  ] },

  // Wood on wood — hollow, pitched, quick.
  clack: { layers: [
    noise(900, 0.05, 0.26, { filter: 'bandpass', q: 5 }),
    tone('sine', 230, 0.10, 0.18, { to: 168, at: 0.003 })
  ] },

  // A stone fist on armour. The loudest thing in the game.
  clang: { layers: [
    noise(3200, 0.045, 0.42, { filter: 'lowpass' }),
    tone('triangle', 330, 0.65, 0.20, { to: 310, at: 0.004 }),
    tone('triangle', 486, 0.48, 0.13, { to: 470, at: 0.006 }),
    tone('sine', 88, 0.30, 0.22, { to: 62, at: 0.002 })
  ] },

  crunch: { layers: [
    noise(1100, 0.08, 0.30, { filter: 'lowpass' }),
    noise(3000, 0.06, 0.14, { filter: 'highpass', at: 0.01 }),
    tone('sine', 110, 0.16, 0.20, { to: 74, at: 0.003 })
  ] },

  // Stone on stone: dry, low, no sustain.
  crack: { layers: [
    noise(880, 0.11, 0.30, { filter: 'lowpass' }),
    noise(420, 0.16, 0.16, { filter: 'bandpass', q: 2, at: 0.01 }),
    tone('sine', 80, 0.19, 0.22, { to: 56, at: 0.003 })
  ] },

  // Flesh. Kept blunt and low rather than graphic.
  rend: { layers: [
    noise(700, 0.09, 0.22, { filter: 'lowpass' }),
    tone('sine', 150, 0.16, 0.14, { to: 70, at: 0.004 })
  ] },

  // No contact at all: a swell that arrives and a tail that leaves.
  swell: { layers: [
    tone('sine', 240, 0.5, 0.14, { to: 1500, attack: 0.12 }),
    tone('triangle', 120, 0.6, 0.10, { to: 700, attack: 0.15 }),
    noise(2200, 0.45, 0.09, { filter: 'bandpass', q: 1.2, at: 0.05 })
  ] },

  /* ---- wreckage landing on the board (wood) ---- */

  landSteel: { layers: [
    noise(3600, 0.04, 0.24, { filter: 'highpass' }),
    noise(2200, 0.07, 0.16, { filter: 'bandpass', q: 6 }),
    tone('triangle', 1180, 0.12, 0.07, { at: 0.004 })
  ] },
  landStone: { layers: [
    noise(520, 0.07, 0.24, { filter: 'lowpass' }),
    tone('sine', 96, 0.10, 0.18, { to: 66, at: 0.003 })
  ] },
  landWood: { layers: [
    noise(820, 0.05, 0.20, { filter: 'bandpass', q: 5 }),
    tone('sine', 205, 0.08, 0.14, { to: 150, at: 0.003 })
  ] },
  // Cloth barely registers — a brush of air, and only just.
  landCloth: { layers: [
    noise(1600, 0.07, 0.035, { filter: 'bandpass', q: 1 })
  ] },
  landGlass: { layers: [
    noise(6500, 0.025, 0.16, { filter: 'highpass' }),
    tone('triangle', 3150, 0.20, 0.09)
  ] },

  /* ---- movement, one per piece ---- */

  stepLeather: { layers: [
    noise(1200, 0.05, 0.14, { filter: 'lowpass' }),
    tone('sine', 98, 0.07, 0.12, { to: 74 })
  ] },
  stepPlate: { layers: [
    noise(900, 0.06, 0.17, { filter: 'lowpass' }),
    tone('sine', 78, 0.10, 0.17, { to: 58 }),
    noise(3400, 0.05, 0.07, { filter: 'bandpass', q: 6, at: 0.02 })
  ] },
  gallop: { layers: [
    noise(700, 0.05, 0.20, { filter: 'lowpass' }),
    tone('sine', 74, 0.10, 0.19, { to: 52 }),
    noise(680, 0.045, 0.14, { filter: 'lowpass', at: 0.07 })
  ] },
  glide: { layers: [
    noise(2000, 0.30, 0.07, { filter: 'bandpass', q: 1 }),
    tone('sine', 300, 0.34, 0.06, { to: 640, attack: 0.06 })
  ] },
  // The golem does not step; it grinds.
  grind: { layers: [
    noise(300, 0.34, 0.16, { filter: 'bandpass', q: 1.5 }),
    tone('sine', 58, 0.30, 0.14, { to: 44 })
  ] },

  /* ---- creature voices ---- */
  /* Formant-ish pairs rather than anything that tries to be a real voice, and
     quiet: these sell the piece, they do not carry the fight. */

  snort: { layers: [
    noise(420, 0.16, 0.15, { filter: 'bandpass', q: 2 }),
    tone('sine', 145, 0.14, 0.09, { to: 88 })
  ] },
  whinny: { layers: [
    tone('triangle', 800, 0.42, 0.10, { to: 520, attack: 0.02 }),
    tone('triangle', 1210, 0.36, 0.06, { to: 780, at: 0.03 }),
    noise(1600, 0.20, 0.05, { filter: 'bandpass', q: 3, at: 0.05 })
  ] },
  groan: { layers: [
    tone('sine', 112, 0.75, 0.12, { to: 68, attack: 0.05 }),
    tone('triangle', 168, 0.60, 0.06, { to: 104, at: 0.03 })
  ] },
  chant: { layers: [
    tone('sine', 132, 1.1, 0.07, { attack: 0.2 }),
    tone('sine', 198, 1.0, 0.04, { attack: 0.25, at: 0.05 })
  ] },
  cry: { layers: [
    tone('triangle', 300, 0.55, 0.09, { to: 900, attack: 0.12 }),
    noise(2600, 0.30, 0.05, { filter: 'bandpass', q: 2, at: 0.1 })
  ] },
  grunt: { layers: [
    noise(600, 0.11, 0.13, { filter: 'lowpass' }),
    tone('sine', 132, 0.13, 0.10, { to: 96 })
  ] },
  shout: { layers: [
    tone('triangle', 262, 0.32, 0.12, { to: 178, attack: 0.02 }),
    noise(1200, 0.18, 0.08, { filter: 'bandpass', q: 2 })
  ] },

  /* ---- attack / hurt voices ---- */
  /* One pair per piece type, synthesized fallbacks for the drop-in sample
     pools in public/audio/sfx/{attack,hurt}/ — see PIECE_SOUND.*.attack and
     .hurt above. Deliberately distinct cue names, even from this same file's
     own charge/cast/effort/fall/death above: attacker and victim can be the
     same piece type (a pawn taking a pawn), and onVoice's once-per-fight
     dedupe keys off this exact string, so a shared name here would silently
     drop whichever fired second. Placeholders in the same spirit as every
     other voice above — quiet, short, tuned by ear, easy to replace with a
     dropped-in recording rather than precious to keep. */

  attackPawn: { layers: [
    noise(900, 0.07, 0.14, { filter: 'bandpass', q: 2 }),
    tone('sine', 220, 0.08, 0.08, { to: 260 })
  ] },
  attackKnight: { layers: [
    noise(420, 0.16, 0.15, { filter: 'bandpass', q: 2 }),
    tone('sine', 150, 0.14, 0.10, { to: 95 })
  ] },
  attackBishop: { layers: [
    tone('sine', 210, 0.5, 0.08, { attack: 0.08 }),
    tone('sine', 315, 0.45, 0.05, { attack: 0.1, at: 0.04 })
  ] },
  attackRook: { layers: [
    noise(500, 0.22, 0.16, { filter: 'lowpass' }),
    tone('sine', 90, 0.24, 0.11, { to: 65, attack: 0.02 })
  ] },
  attackQueen: { layers: [
    tone('triangle', 340, 0.4, 0.09, { to: 700, attack: 0.06 }),
    noise(2200, 0.18, 0.04, { filter: 'bandpass', q: 3, at: 0.06 })
  ] },
  attackKing: { layers: [
    tone('triangle', 220, 0.28, 0.13, { to: 150, attack: 0.015 }),
    noise(1000, 0.16, 0.07, { filter: 'bandpass', q: 2 })
  ] },

  hurtPawn: { layers: [
    noise(1400, 0.05, 0.13, { filter: 'highpass' }),
    tone('sine', 300, 0.06, 0.07, { to: 220 })
  ] },
  hurtKnight: { layers: [
    tone('triangle', 700, 0.28, 0.09, { to: 480, attack: 0.015 }),
    noise(1400, 0.14, 0.04, { filter: 'bandpass', q: 3, at: 0.03 })
  ] },
  hurtBishop: { layers: [
    tone('sine', 260, 0.35, 0.07, { to: 190, attack: 0.03 })
  ] },
  hurtRook: { layers: [
    tone('sine', 100, 0.5, 0.11, { to: 70, attack: 0.04 }),
    tone('triangle', 150, 0.4, 0.05, { to: 105, at: 0.02 })
  ] },
  hurtQueen: { layers: [
    tone('triangle', 500, 0.32, 0.08, { to: 700, attack: 0.01 }),
    noise(2400, 0.16, 0.04, { filter: 'bandpass', q: 2, at: 0.05 })
  ] },
  hurtKing: { layers: [
    tone('triangle', 240, 0.26, 0.11, { to: 165, attack: 0.012 }),
    noise(900, 0.15, 0.06, { filter: 'bandpass', q: 2 })
  ] },

  /* ---- the game speaking to the player ---- */
  /* Deliberately tonal and unlike anything in a fight, so the player can still
     read the game by ear when the board is loud. */

  select: { layers: [tone('triangle', 660, 0.06, 0.12)] },
  // A destination-square click, not the footstep-ish 'move' below (which
  // fires later, once the piece actually arrives) — a slightly higher tick
  // reads as "confirmed" rather than as a duplicate of 'select'.
  confirm: { layers: [tone('triangle', 880, 0.05, 0.10)] },
  move: { layers: [tone('triangle', 440, 0.08, 0.10)] },
  check: { layers: [
    tone('triangle', 320, 0.30, 0.18),
    tone('triangle', 480, 0.26, 0.08, { at: 0.05 })
  ] },
  win: { layers: [
    tone('triangle', 523, 0.9, 0.14),
    tone('triangle', 659, 0.9, 0.12, { at: 0.11 }),
    tone('triangle', 784, 0.9, 0.11, { at: 0.22 })
  ] },
  lose: { layers: [
    tone('triangle', 392, 1.1, 0.14),
    tone('triangle', 311, 1.1, 0.12, { at: 0.11 }),
    tone('triangle', 233, 1.1, 0.11, { at: 0.22 })
  ] }
};
